'use client'

import { useState } from 'react'

import { Box } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { AiOperatorIcon } from '../Icon'
import { Tooltip } from '../Tooltip'
import { aiGlyphCss } from './OperatorAvatar.styles'
import { UserAvatar } from './UserAvatar'
import { SIZE, UserAvatarSize, getBg } from './UserAvatar.styles'

/**
 * The actor name the backend reports for AI-Operator activity. Compared by
 * value because the API returns the AI Operator as an ordinary creator with
 * this first/last name rather than a distinct type.
 */
export const AI_OPERATOR_NAME = 'AI Operator'

export interface OperatorAvatarIProps {
  /** Actor's display name. Exactly `'AI Operator'` selects the AI badge. */
  name: string | null
  size?: UserAvatarSize
  colorScheme?: ColorScheme
  /**
   * Forces the AI badge regardless of `name`. Needed where the AI Operator is
   * the actor but `name` holds someone else — an emailed notification names its
   * recipient, and the legacy feed showed the AI avatar on those rows too.
   */
  isAiOperator?: boolean
}

/**
 * Avatar for whoever performed an action: the AI Operator's glyph, or a human's
 * initials via {@link UserAvatar}. Callers pass a name and get the right
 * treatment, so no surface has to re-derive "is this the AI" for itself.
 */
export const OperatorAvatar = ({
  name,
  size = 'sm',
  colorScheme = 'dark',
  isAiOperator = false,
}: OperatorAvatarIProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  if (!isAiOperator && name !== AI_OPERATOR_NAME) {
    return <UserAvatar colorScheme={colorScheme} name={name} size={size} />
  }

  const { diameter } = SIZE[size]

  return (
    <>
      {/* Sizing goes through `css`, not `sx` — this package is on Chakra v3. */}
      <Box
        alignItems='center'
        aria-label={AI_OPERATOR_NAME}
        bg={getBg(colorScheme)}
        borderRadius='999px'
        color='text.on-accent'
        css={aiGlyphCss(size)}
        data-testid='operator-avatar-ai'
        display='inline-flex'
        flexShrink={0}
        h={diameter}
        justifyContent='center'
        onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
        onMouseLeave={() => setAnchorEl(null)}
        role='img'
        userSelect='none'
        w={diameter}
      >
        <AiOperatorIcon strokeColor='currentColor' />
      </Box>
      {anchorEl && (
        <Tooltip
          anchorEl={anchorEl}
          colorScheme={colorScheme}
          label={AI_OPERATOR_NAME}
        />
      )}
    </>
  )
}
